/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, Button, Spinner, Alert, Badge, Table,
} from 'react-bootstrap';
import dayjs from 'dayjs';
import FlightService from '../../../Services/FlightService';
import extractFlightNumber from '../../../Utils/extractFlightNumber';

// Format flight time for display
const formatFlightTime = (isoString) => {
  if (!isoString) return '-';
  // Extract date and time from ISO string without timezone conversion
  const match = isoString.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (match) {
    return `${match[1]} ${match[2]}`;
  }
  return isoString;
};

// Get status badge color
const getFlightStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'landed':
    case 'arrived':
      return 'success';
    case 'delayed':
      return 'warning';
    case 'cancelled':
      return 'danger';
    case 'scheduled':
    case 'active':
    case 'en route':
      return 'info';
    default:
      return 'secondary';
  }
};

function GroupFlightDetailsModal({
  show, onHide, groupName, bookings,
}) {
  const [flightData, setFlightData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Collect all unique flight numbers from bookings and passenger manifests
  const getUniqueFlights = useCallback(() => {
    const flights = new Map(); // Map of flightNumber -> { passengers: [], bookingId }

    bookings.forEach((booking) => {
      // Main booking flight number
      if (booking.flight_number) {
        const flightCode = extractFlightNumber(booking.flight_number);
        if (flightCode && flightCode.length >= 3) {
          if (!flights.has(flightCode)) {
            flights.set(flightCode, { passengers: [], pickupDate: booking.date_datetime });
          }
          flights.get(flightCode).passengers.push({
            name: booking.full_name || 'Unknown',
            bookingId: booking._id,
            type: 'booking',
          });
        }
      }

      // Passenger manifest flight numbers
      if (booking.passenger_manifest?.length > 0) {
        booking.passenger_manifest.forEach((passenger) => {
          if (passenger.flight_number) {
            const flightCode = extractFlightNumber(passenger.flight_number);
            if (flightCode && flightCode.length >= 3) {
              if (!flights.has(flightCode)) {
                flights.set(flightCode, { passengers: [], pickupDate: booking.date_datetime });
              }
              flights.get(flightCode).passengers.push({
                name: passenger.name || 'Unknown',
                bookingId: booking._id,
                type: 'manifest',
              });
            }
          }
        });
      }
    });

    return flights;
  }, [bookings]);

  // Fetch flight details for all unique flights
  const fetchAllFlightDetails = useCallback(async () => {
    const flights = getUniqueFlights();

    if (flights.size === 0) {
      setFlightData({});
      return;
    }

    setIsLoading(true);
    setError('');

    const results = {};

    // Fetch all flights in parallel
    const promises = Array.from(flights.entries()).map(async ([flightCode, data]) => {
      try {
        const validation = await FlightService.lookupFlight(
          flightCode,
          '',
          data.pickupDate ? dayjs(data.pickupDate).format('YYYY-MM-DD HH:mm') : '',
        );
        results[flightCode] = {
          ...validation,
          passengers: data.passengers,
        };
      } catch (err) {
        results[flightCode] = {
          found: false,
          error: 'Lookup failed',
          passengers: data.passengers,
        };
      }
    });

    await Promise.all(promises);
    setFlightData(results);
    setIsLoading(false);
  }, [getUniqueFlights]);

  // Fetch flights when modal opens
  useEffect(() => {
    if (show && bookings?.length > 0) {
      fetchAllFlightDetails();
    }
  }, [show, bookings, fetchAllFlightDetails]);

  // Sort flights by arrival time
  const sortedFlights = Object.entries(flightData).sort((a, b) => {
    const aTime = a[1]?.flight?.arrival?.scheduled || '';
    const bTime = b[1]?.flight?.arrival?.scheduled || '';
    return aTime.localeCompare(bTime);
  });

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-plane me-2" />
          Flight Details -
          {' '}
          {groupName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mt-2">Loading flight information...</p>
          </div>
        )}

        {error && (
          <Alert variant="danger">{error}</Alert>
        )}

        {!isLoading && sortedFlights.length === 0 && (
          <Alert variant="info">
            <i className="fa fa-info-circle me-2" />
            No flight numbers found in this group&apos;s bookings.
          </Alert>
        )}

        {!isLoading && sortedFlights.length > 0 && (
          <div className="table-responsive">
            <Table striped hover>
              <thead className="table-dark">
                <tr>
                  <th>Flight</th>
                  <th>Airline</th>
                  <th>Route</th>
                  <th>Scheduled Arrival</th>
                  <th>Estimated Arrival</th>
                  <th>Status</th>
                  <th>Passengers</th>
                </tr>
              </thead>
              <tbody>
                {sortedFlights.map(([flightCode, data]) => (
                  <tr key={flightCode}>
                    <td>
                      <div className="d-flex align-items-center">
                        {data.flight && (
                          <img
                            src={`https://pics.avs.io/60/60/${flightCode.substring(0, 2)}.png`}
                            alt=""
                            style={{ width: 30, height: 30, marginRight: 8 }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <strong>{flightCode}</strong>
                      </div>
                    </td>
                    <td>
                      {data.found && data.flight ? (
                        data.flight.airline
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {data.found && data.flight ? (
                        <span>
                          <strong>{data.flight.departure.iata}</strong>
                          {' '}
                          <i className="fa fa-arrow-right mx-1 text-muted" />
                          {' '}
                          <strong>{data.flight.arrival.iata}</strong>
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {data.found && data.flight?.arrival?.scheduled ? (
                        formatFlightTime(data.flight.arrival.scheduled)
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {data.found && data.flight?.arrival?.estimated ? (
                        <span className="text-success fw-bold">
                          {formatFlightTime(data.flight.arrival.estimated)}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {data.found && data.flight ? (
                        <Badge bg={getFlightStatusColor(data.flight.status)}>
                          {data.flight.status || 'Unknown'}
                        </Badge>
                      ) : (
                        <Badge bg="secondary">Not Found</Badge>
                      )}
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {data.passengers?.map((p) => (
                          <Badge
                            key={`${p.bookingId}-${p.name}`}
                            bg={p.type === 'booking' ? 'primary' : 'info'}
                            className="me-1"
                            title={p.type === 'booking' ? 'Main booking contact' : 'From passenger manifest'}
                          >
                            {p.name}
                          </Badge>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {/* Warnings Summary */}
        {!isLoading && sortedFlights.some(([, data]) => data.warnings?.length > 0) && (
          <Alert variant="warning" className="mt-3">
            <strong>
              <i className="fa fa-exclamation-triangle me-2" />
              Warnings:
            </strong>
            <ul className="mb-0 mt-2">
              {sortedFlights
                .filter(([, data]) => data.warnings?.length > 0)
                .map(([flightCode, data]) => (
                  data.warnings.map((warning) => (
                    <li key={`${flightCode}-${warning}`}>
                      <strong>
                        {flightCode}
                        :
                      </strong>
                      {' '}
                      {warning}
                    </li>
                  ))
                ))}
            </ul>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={fetchAllFlightDetails} disabled={isLoading}>
          <i className={`fa fa-refresh ${isLoading ? 'fa-spin' : ''} me-2`} />
          Refresh Flights
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default GroupFlightDetailsModal;
